import { useEffect, useRef, useState } from 'react';

/**
 * A name that renames what it names, written where it is read: pressing it
 * turns it into the field, Enter or leaving keeps the new name, Esc keeps the
 * old one. Sits inside whatever heading the page gives it, in that heading's
 * type.
 */
export function EditableName({
  name,
  onRename,
  placeholder,
  unnamed = false,
  clearable = false,
  renameSignal = 0,
}: {
  name: string;
  onRename: (name: string) => void;
  placeholder?: string;
  /** Nobody has named it: [placeholder] reads in its place, and the field
   *  starts empty rather than holding a stand-in. */
  unnamed?: boolean;
  /** An empty draft on a named item clears the name instead of resetting it. */
  clearable?: boolean;
  /** Starts the edit from outside, each time it grows: a menu's Rename. */
  renameSignal?: number;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  /** Esc and Enter both end the edit; the blur that follows must not decide
   *  it a second time. */
  const settled = useRef(false);

  useEffect(() => {
    if (renameSignal === 0) return;
    settled.current = false;
    setDraft(unnamed ? '' : name);
    // Only a new signal starts an edit; the name may change under one already going.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renameSignal]);

  const finish = (keep: boolean) => {
    if (settled.current || draft === null) return;
    settled.current = true;
    const next = draft.replace(/\s+/g, ' ').trim();
    setDraft(null);
    if (!keep) return;
    if (next) {
      if (unnamed || next !== name) onRename(next);
    } else if (clearable && !unnamed) {
      onRename('');
    }
  };

  if (draft === null) {
    return (
      <button
        type="button"
        className={`title-rename${unnamed ? ' is-unnamed' : ''}`}
        title="Rename"
        onClick={() => {
          settled.current = false;
          setDraft(unnamed ? '' : name);
        }}
      >
        {unnamed ? (placeholder ?? name) : name}
      </button>
    );
  }

  return (
    <input
      className="title-rename-field"
      aria-label="Name"
      value={draft}
      placeholder={placeholder}
      autoFocus
      onFocus={(e) => e.currentTarget.select()}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          finish(true);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          finish(false);
        }
      }}
      onBlur={() => finish(true)}
    />
  );
}
